import React, { useEffect, useState } from "react";
import request from "superagent";
import crypto from "crypto-browserify";

import makeid from "../../utils/makeid";
import logger from "../../utils/LoggerFrontend";
import Actions from "../../Actions";
import {
  ALLOWED_VIDEO_FORMATS,
  ALLOWED_AUDIO_FORMATS,
  ALLOWED_IMAGE_FORMATS
} from "../../PagesUser/SelfServiceCampaignCreation/Previews/constants";
import { UploadIcon } from "../Icons";
import {
  CLOUDINARY_API_KEY,
  CLOUDINARY_API_SECRET,
  CLOUDINARY_UPLOAD_URL,
  FileUpload,
  Input,
  MAX_SIZE_IMAGE,
  MAX_SIZE_VIDEO,
  Spinner
} from "./Upload.styled";

export const Upload = ({
  image,
  allowVideos,
  allowImages = true,
  fileTypes = [],
  folderName,
  context: fileContext,
  isLogo,
  showPreview,
  showLoading,
  label,
  buttonText,
  useUploadIcon,
  type,
  handlePictureChanged = () => {}
}) => {
  const [upload, setUpload] = useState({ url: image || "" });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (image !== upload?.url) {
      setUpload(image);
    }
  }, [image, upload]);

  const onFileChange = (event) => {
    let allowedFiles = ALLOWED_IMAGE_FORMATS.concat(ALLOWED_VIDEO_FORMATS)
      .concat(ALLOWED_AUDIO_FORMATS)
      .concat(fileTypes || []);

    if (!allowVideos) {
      allowedFiles = allowedFiles.filter((type) => !ALLOWED_VIDEO_FORMATS.includes(type));
    }

    if (!allowImages) {
      allowedFiles = allowedFiles.filter((type) => !ALLOWED_IMAGE_FORMATS.includes(type));
    }

    const input = event.target;
    const uploadedFile = input?.files[0];

    if (!input || !uploadedFile) {
      logger.log("error:Upload:onFileChange", "No input file found", {
        input,
        uploadedFile
      });
    }

    const extension = uploadedFile?.name?.split(".").pop().toLowerCase();
    const isAcceptableType = allowedFiles.indexOf(extension) > -1;
    const isAudio = ALLOWED_AUDIO_FORMATS.includes(extension);
    const limit = ALLOWED_VIDEO_FORMATS.includes(extension) ? MAX_SIZE_VIDEO : MAX_SIZE_IMAGE;
    const limitInMB = limit / 1e6;

    let fileSizeIsValid = true;

    if (uploadedFile?.size > limit) {
      fileSizeIsValid = false;
      Actions.addNotification(`Das Bild ist zu groß (weniger < ${limitInMB} MB)`, "error");
    } else if (!isAcceptableType) {
      Actions.addNotification(
        `Invalid file type, allowed types: ${allowedFiles.join(", ")}`,
        "error"
      );
    }

    if (uploadedFile && fileSizeIsValid && isAcceptableType) {
      const reader = new FileReader();
      setUploading(true);

      reader.onload = () => {
        logger.log("Upload:onFileChange", "Start uploading file...upload:", {
          file: uploadedFile.name,
          folderName: folderName,
          context: fileContext
        });

        // Upload to cloudinary
        const timestamp = (Date.now() / 1000) | 0;
        const contextValues = [];

        if (isLogo) {
          contextValues.push("type=logo");
          contextValues.push("assetType=image");
        } else if (ALLOWED_IMAGE_FORMATS.includes(extension)) {
          contextValues.push("type=image");
          contextValues.push("assetType=image");
        } else if (ALLOWED_VIDEO_FORMATS.includes(extension)) {
          contextValues.push("type=video");
          contextValues.push("assetType=video");
        } else if (isAudio) {
          contextValues.push("type=audio");
          contextValues.push("assetType=audio");
        }

        if (fileContext) {
          contextValues.push(fileContext);
        }

        let params = `timestamp=${timestamp}`;

        if (!isAudio) {
          params += "&transformation=q_auto";
        }

        // WARNING: the order of the string matters
        if (folderName) {
          params = `folder=${folderName || ""}&${params}`;
        }

        const context = contextValues.join("|");
        params = `context=${context}&${params}`;

        const signature = crypto
          .createHash("sha1")
          .update(`${params}${CLOUDINARY_API_SECRET}`)
          .digest("hex");

        logger.log("Upload:onFileChange", "Start upload", {
          context,
          folderName: folderName,
          signature,
          timestamp,
          uploadedFile,
          CLOUDINARY_API_KEY
        });

        let uploadReq = request
          .post(`${CLOUDINARY_UPLOAD_URL}/auto/upload`)
          .field("context", context || "")
          .field("folder", folderName || "")
          .field("api_key", CLOUDINARY_API_KEY)
          .field("signature", signature)
          .field("timestamp", timestamp)
          .field("file", uploadedFile);

        // If it's an audio file, we don't want to compress it
        if (!isAudio) {
          uploadReq = uploadReq.field("transformation", "q_auto");
        }

        uploadReq.end((err, response) => {
          if (err) {
            logger.log("error:Upload:onFileChange", "selectedFile...upload:", {
              err,
              response
            });

            Actions.addNotification(
              "Es ist ein Fehler beim hochladen der Datei aufgetreten.",
              "error"
            );
            return;
          }

          const { body } = response;

          if (body.secure_url) {
            logger.log("Upload:onFileChange", "Finished uploading image", { body });

            setUploading(false);

            const data = {
              name: body.public_id,
              url: body.secure_url,
              extension: body.format,
              height: body.height,
              width: body.width,
              type: "upload",
              etag: body.etag || "",
              phash: body.phash || ""
            };

            // Make sure to return the `type` as the 2nd argument,
            // since some of the callers use this information.
            handlePictureChanged(data, type || "Upload");

            Actions.addNotification("Das Datei wurde hochgeladen");

            setUpload(data);
          } else {
            logger.log("error:Upload:onFileChange", "No secure URL found", {
              response
            });
          }
        });
      };

      reader.readAsDataURL(uploadedFile);
    }
  };

  const uploadId = makeid();

  return (
    <div>
      {showPreview && !showLoading && (
        <span>
          {label}
          <br />
        </span>
      )}

      {showPreview && upload?.url && (
        <div>
          <img src={upload.url} style={{ width: 100 }} />
        </div>
      )}

      <FileUpload>
        <Input id={"upload-btn-" + uploadId} type="file" onChange={onFileChange} />
        {showLoading && uploading ? (
          <Spinner />
        ) : (
          <label htmlFor={"upload-btn-" + uploadId}>
            {useUploadIcon ? <UploadIcon /> : buttonText || "+ Datei hochladen"}
          </label>
        )}
      </FileUpload>
    </div>
  );
};
